import { ChangeEvent, useRef, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import Chip from "@mui/material/Chip";
import FutureBoardProgress from "./FutureBoardProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import ImageList from "@mui/material/ImageList";
import ImageListItem from "@mui/material/ImageListItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {
  MAX_IMAGES_PER_CARD, addItem, canAdd, checkImageFile, deleteFutureImage, fromPreset, fromUpload, hasItem, listFuturePresets,
  makeCoverItem, moveItem, parseFutureBoardError, removeItem, updateFutureImage, uploadFutureImage, withSettings,
  type EditorImage, type FutureBoardFailure, type FuturePreset,
} from "@chronospark/shared";
import FailureAlert from "./FailureAlert";

type Translate = (key: string, options?: object) => string;

interface Props {
  items: EditorImage[];
  onChange: (items: EditorImage[]) => void;
  accessToken?: string;
  language: string;
  t: Translate;
  /** 自分の写真の登録は Pro のみ（画面の案内用。最終的な判断はサーバー） */
  isPro: boolean;
  onRequirePro: () => void;
  onNotice: (message: string) => void;
  disabled?: boolean;
}

const ALT_MAX = 120;

// 画像の選択と整理（最大3枚。先頭が表紙）。プリセットは本人が押したときだけ一覧を取得する（自動の推薦はしない）。
function ImagePicker({ items, onChange, accessToken, language, t, isPro, onRequirePro, onNotice, disabled }: Props) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [proNoticeOpen, setProNoticeOpen] = useState(false);
  const paywallAfterNotice = useRef(false);
  const [presetsOpen, setPresetsOpen] = useState(false);
  const [presets, setPresets] = useState<FuturePreset[] | null>(null);
  const [uploading, setUploading] = useState(false);
  const [failure, setFailure] = useState<FutureBoardFailure | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [settingsKey, setSettingsKey] = useState<string | null>(null);
  const full = !canAdd(items);

  const openPresets = async () => {
    setPresetsOpen(true);
    setFailure(null);
    if (presets || !accessToken) return;
    try {
      setPresets((await listFuturePresets(accessToken)).presets);
    } catch (error) {
      setFailure(parseFutureBoardError(error));
    }
  };

  const choosePreset = (preset: FuturePreset) => {
    onChange(addItem(items, fromPreset({ key: preset.key, alt_text: language.startsWith("en") ? preset.alt_text_en : preset.alt_text, thumb_url: preset.thumb_url })));
    setPresetsOpen(false);
  };

  // Pro 専用であることを先に案内し、本人が選んだ場合だけ paywall に進む。
  const startUpload = () => {
    setProblem(null);
    if (!isPro) {
      setProNoticeOpen(true);
      return;
    }
    fileInput.current?.click();
  };

  const onFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !accessToken) return;
    const check = checkImageFile({ size: file.size, type: file.type });
    if (check) {
      setProblem(t(check === "too_large" ? "futureBoard.image.tooLarge" : "futureBoard.image.unsupported"));
      return;
    }
    setUploading(true);
    setFailure(null);
    try {
      const { image } = await uploadFutureImage(accessToken, file);
      onChange(addItem(items, fromUpload(image)));
    } catch (error) {
      const parsed = parseFutureBoardError(error);
      if (parsed.kind === "requires_pro") setProNoticeOpen(true);
      else setFailure(parsed);
    } finally {
      setUploading(false);
    }
  };

  const remove = (index: number) => {
    const { items: next, discard } = removeItem(items, index);
    onChange(next);
    // まだカードに保存していない自分の写真は、サーバーにも残さない（失敗しても、あとで自動で片づく）
    if (discard?.id && accessToken) void deleteFutureImage(accessToken, discard.id).catch(() => undefined);
  };

  const settingsItem = items.find(item => item.key === settingsKey) ?? null;

  return (
    <Box>
      <Typography variant="subtitle1" component="h3" sx={{ mb: 1 }}>
        {t("futureBoard.pickers.section", { max: MAX_IMAGES_PER_CARD })}
      </Typography>

      {failure && <FailureAlert failure={failure} t={t} onClose={() => setFailure(null)} />}
      {problem && <Alert severity="warning" onClose={() => setProblem(null)} sx={{ mb: 2 }}>{problem}</Alert>}

      {items.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t("futureBoard.pickers.none")}</Typography>
      ) : (
        <Stack spacing={1.5} sx={{ mb: 2 }}>
          {items.map((item, index) => (
            <Stack key={item.key} direction="row" spacing={1.5} alignItems="center" sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5, p: 1 }}>
              <Box
                component="img"
                src={item.thumbUrl ?? undefined}
                alt={item.alt ?? t("futureBoard.image.defaultAlt")}
                sx={{ width: 88, height: 66, objectFit: "cover", objectPosition: `${item.focalX}% ${item.focalY}%`, borderRadius: 1, bgcolor: "grey.100", flexShrink: 0 }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {index === 0 && <Chip size="small" color="info" label={t("futureBoard.pickers.cover")} sx={{ mb: 0.5 }} />}
                <Typography variant="body2" noWrap>{item.alt ?? t("futureBoard.image.defaultAlt")}</Typography>
              </Box>
              <Stack direction="row" alignItems="center" flexWrap="wrap" justifyContent="flex-end">
                {index > 0 && (
                  <Button size="small" onClick={() => onChange(makeCoverItem(items, index))} disabled={disabled}>{t("futureBoard.pickers.makeCover")}</Button>
                )}
                <IconButton size="small" onClick={() => onChange(moveItem(items, index, index - 1))} disabled={disabled || index === 0} aria-label={`${t("futureBoard.pickers.earlier")}: ${item.alt ?? t("futureBoard.image.defaultAlt")}`}>
                  <Icon fontSize="small">arrow_upward</Icon>
                </IconButton>
                <IconButton size="small" onClick={() => onChange(moveItem(items, index, index + 1))} disabled={disabled || index === items.length - 1} aria-label={`${t("futureBoard.pickers.later")}: ${item.alt ?? t("futureBoard.image.defaultAlt")}`}>
                  <Icon fontSize="small">arrow_downward</Icon>
                </IconButton>
                <Button size="small" onClick={() => setSettingsKey(item.key)} disabled={disabled}>{t("futureBoard.pickers.settings")}</Button>
                <Button size="small" color="error" onClick={() => remove(index)} disabled={disabled}>{t("futureBoard.pickers.remove")}</Button>
              </Stack>
            </Stack>
          ))}
        </Stack>
      )}

      {full && <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>{t("futureBoard.image.maxImages", { max: MAX_IMAGES_PER_CARD })}</Typography>}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
        <Button variant="outlined" onClick={openPresets} disabled={disabled || full} startIcon={<Icon>photo_library</Icon>}>
          {t("futureBoard.image.fromPresets")}
        </Button>
        <Button variant="outlined" onClick={startUpload} disabled={disabled || full || uploading} startIcon={uploading ? <FutureBoardProgress size={16} /> : <Icon>add_photo_alternate</Icon>}>
          {uploading ? t("futureBoard.pickers.uploading") : t("futureBoard.image.fromPhotos")}
        </Button>
        <input ref={fileInput} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={onFile} data-testid="future-board-file" />
      </Stack>
      {!isPro && <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>{t("futureBoard.image.proOnly")}</Typography>}

      <Dialog
        open={proNoticeOpen}
        onClose={() => setProNoticeOpen(false)}
        aria-labelledby="future-photo-pro-title"
        aria-describedby="future-photo-pro-description"
        maxWidth="xs"
        fullWidth
        TransitionProps={{ onExited: () => {
          if (paywallAfterNotice.current) {
            paywallAfterNotice.current = false;
            onRequirePro();
          }
        } }}
      >
        <DialogTitle id="future-photo-pro-title">{t("futureBoard.image.proNoticeTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText id="future-photo-pro-description">{t("futureBoard.image.proNoticeBody")}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" autoFocus onClick={() => setProNoticeOpen(false)}>{t("futureBoard.actions.cancel")}</Button>
          <Button variant="contained" onClick={() => {
            paywallAfterNotice.current = true;
            setProNoticeOpen(false);
          }}>{t("futureBoard.image.viewProPlan")}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={presetsOpen} onClose={() => setPresetsOpen(false)} aria-labelledby="future-presets-title" maxWidth="md" fullWidth scroll="paper">
        <DialogTitle id="future-presets-title">{t("futureBoard.pickers.presetTitle")}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t("futureBoard.pickers.presetHint")}</Typography>
          {failure && <FailureAlert failure={failure} t={t} />}
          {!presets && !failure && <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><FutureBoardProgress aria-label={t("Common.loading")} /></Box>}
          {presets && presets.length === 0 && <Typography color="text.secondary">{t("futureBoard.pickers.presetEmpty")}</Typography>}
          {presets && presets.length > 0 && (
            <ImageList cols={3} gap={12} sx={{ m: 0, "@media (max-width:600px)": { gridTemplateColumns: "repeat(2, 1fr) !important" } }}>
              {presets.map(preset => {
                const added = hasItem(items, `preset:${preset.key}`);
                const label = language.startsWith("en") ? preset.alt_text_en : preset.alt_text;
                return (
                  <ImageListItem key={preset.key}>
                    <ButtonBase
                      onClick={() => choosePreset(preset)}
                      disabled={added}
                      aria-label={added ? `${label}（${t("futureBoard.pickers.presetAdded")}）` : label}
                      sx={{ display: "block", borderRadius: 1, overflow: "hidden", opacity: added ? 0.5 : 1, textAlign: "left" }}
                    >
                      <Box component="img" src={preset.thumb_url ?? undefined} alt="" loading="lazy" sx={{ display: "block", width: "100%", aspectRatio: "4 / 3", objectFit: "cover" }} />
                      <Typography variant="caption" sx={{ display: "block", p: 0.5 }}>{added ? `${label}（${t("futureBoard.pickers.presetAdded")}）` : label}</Typography>
                    </ButtonBase>
                  </ImageListItem>
                );
              })}
            </ImageList>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPresetsOpen(false)}>{t("futureBoard.actions.close")}</Button>
        </DialogActions>
      </Dialog>

      <ImageSettingsDialog
        item={settingsItem}
        accessToken={accessToken}
        t={t}
        onClose={() => setSettingsKey(null)}
        onSaved={(key, settings) => {
          onChange(withSettings(items, key, settings));
          onNotice(t("futureBoard.pickers.settingsSaved"));
          setSettingsKey(null);
        }}
      />
    </Box>
  );
}

// 位置の調整。アプリのテーマでは MUI の Slider が描画に失敗するため、標準の range を使う。
function PositionRange({ id, label, value, onChange, disabled }: { id: string; label: string; value: number; onChange: (value: number) => void; disabled: boolean }) {
  return (
    <Box sx={{ mb: 1 }}>
      <Typography component="label" htmlFor={id} variant="body2">{label}（{value}）</Typography>
      <Box
        component="input"
        id={id}
        type="range"
        min={0}
        max={100}
        value={value}
        disabled={disabled}
        onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(Number(event.target.value))}
        sx={{ display: "block", width: "100%" }}
      />
    </Box>
  );
}

interface SettingsProps {
  item: EditorImage | null;
  accessToken?: string;
  t: Translate;
  onClose: () => void;
  onSaved: (key: string, settings: { alt: string | null; focalX: number; focalY: number }) => void;
}

// 画像の位置（一覧での切り抜き）と説明。保存済みの画像（サーバーに行がある画像）だけ変更できる。
function ImageSettingsDialog({ item, accessToken, t, onClose, onSaved }: SettingsProps) {
  const [alt, setAlt] = useState("");
  const [x, setX] = useState(50);
  const [y, setY] = useState(50);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<FutureBoardFailure | null>(null);
  const [loadedKey, setLoadedKey] = useState<string | null>(null);

  if (item && loadedKey !== item.key) {
    setLoadedKey(item.key);
    setAlt(item.alt ?? "");
    setX(item.focalX);
    setY(item.focalY);
    setFailure(null);
  }
  if (!item && loadedKey !== null) setLoadedKey(null);

  const tooLong = [...alt].length > ALT_MAX;
  const canEdit = item?.id !== null && item?.id !== undefined;

  const save = async () => {
    if (!item || item.id === null || !accessToken || tooLong) return;
    setBusy(true);
    setFailure(null);
    try {
      const text = alt.trim() ? alt.trim() : null;
      await updateFutureImage(accessToken, item.id, { alt_text: text, focal_x: x, focal_y: y });
      onSaved(item.key, { alt: text, focalX: x, focalY: y });
    } catch (error) {
      setFailure(parseFutureBoardError(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={Boolean(item)} onClose={busy ? undefined : onClose} aria-labelledby="future-image-settings-title" maxWidth="sm" fullWidth>
      <DialogTitle id="future-image-settings-title">{t("futureBoard.pickers.settingsTitle")}</DialogTitle>
      <DialogContent>
        {item && (
          <Box component="img" src={item.thumbUrl ?? undefined} alt="" sx={{ display: "block", width: "100%", maxHeight: 200, objectFit: "cover", objectPosition: `${x}% ${y}%`, borderRadius: 1, mb: 2, bgcolor: "grey.100" }} />
        )}
        {!canEdit && <Alert severity="info" sx={{ mb: 2 }}>{t("futureBoard.pickers.settingsAfterSave")}</Alert>}
        {failure && <FailureAlert failure={failure} t={t} />}
        <TextField
          fullWidth
          label={t("futureBoard.pickers.altLabel")}
          value={alt}
          onChange={event => setAlt(event.target.value)}
          error={tooLong}
          helperText={tooLong ? t("futureBoard.validation.too_long", { max: ALT_MAX }) : t("futureBoard.pickers.altHelp")}
          disabled={!canEdit || busy}
          sx={{ mb: 2 }}
        />
        <PositionRange id="focal-x" label={t("futureBoard.pickers.focalX")} value={x} onChange={setX} disabled={!canEdit || busy} />
        <PositionRange id="focal-y" label={t("futureBoard.pickers.focalY")} value={y} onChange={setY} disabled={!canEdit || busy} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>{t("futureBoard.actions.cancel")}</Button>
        <Button onClick={save} disabled={!canEdit || busy || tooLong} variant="contained">
          {busy && <FutureBoardProgress size={18} color="inherit" />}{t("futureBoard.actions.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ImagePicker;
